import { Clock, Flame, KeyRound, Mail, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/lib/i18n";

const EXPIRY_OPTIONS = [
  { value: "1h", tKey: "expiry1h" },
  { value: "1d", tKey: "expiry1d" },
  { value: "7d", tKey: "expiry7d" },
  { value: "30d", tKey: "expiry30d" },
];

export default function OptionsPanel({
  expiry, setExpiry,
  oneTime, setOneTime,
  usePassword, setUsePassword,
  password, setPassword,
  useNotify, setUseNotify,
  notifyEmail, setNotifyEmail,
  emailEnabled,
  submitting,
  canSubmit,
  onSubmit,
}) {
  const { t } = useLang();
  return (
    <div className="p-6 md:p-8 space-y-6 bg-neutral-100 dark:bg-[#0d0d0d]">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">
          {"// " + t("optionsTitle")}
        </div>

        {/* Expiration */}
        <div className="space-y-2 mb-6">
          <Label className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300 text-sm">
            <Clock className="w-3.5 h-3.5" /> {t("expiry")}
          </Label>
          <Select value={expiry} onValueChange={setExpiry}>
            <SelectTrigger
              data-testid="expiry-select"
              className="bg-transparent border border-black/10 dark:border-white/10 focus:ring-1 focus:ring-black/30 dark:focus:ring-white/30 rounded-none font-mono"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#0a0a0a] border border-black/10 dark:border-white/10 rounded-none">
              {EXPIRY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`expiry-${o.value}`}>
                  {t(o.tKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* One-time */}
        <ToggleRow
          testid="one-time-switch"
          iconEl={<Flame className="w-3.5 h-3.5 text-[#ff3b30]" />}
          labelKey="oneTime"
          hintKey="oneTimeHint"
          checked={oneTime}
          onCheckedChange={setOneTime}
        />

        {/* Password */}
        <div className="py-4 border-t border-black/10 dark:border-white/10">
          <ToggleRow
            testid="password-switch"
            iconEl={<KeyRound className="w-3.5 h-3.5" />}
            labelKey="passwordProtect"
            hintKey="passwordHint"
            checked={usePassword}
            onCheckedChange={setUsePassword}
            noBorder
          />
          {usePassword && (
            <Input
              data-testid="password-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("password")}
              className="mt-3 bg-transparent border border-black/10 dark:border-white/10 rounded-none font-mono"
            />
          )}
        </div>

        {/* Notify */}
        <div className="py-4 border-t border-black/10 dark:border-white/10">
          <ToggleRow
            testid="notify-switch"
            iconEl={<Mail className="w-3.5 h-3.5" />}
            labelKey="notify"
            hintKey="notifyHint"
            checked={useNotify}
            onCheckedChange={setUseNotify}
            disabled={!emailEnabled}
            noBorder
          />
          {useNotify && (
            <Input
              data-testid="notify-email-input"
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder={t("recipient")}
              className="mt-3 bg-transparent border border-black/10 dark:border-white/10 rounded-none font-mono"
            />
          )}
          {!emailEnabled && (
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-600 mt-2">
              {t("emailFeatureDisabled")}
            </p>
          )}
        </div>
      </div>

      <Button
        data-testid="encrypt-button"
        onClick={onSubmit}
        disabled={!canSubmit}
        className="w-full h-12 rounded-none bg-black dark:bg-white text-white dark:text-black hover:bg-[#00ff66] hover:text-white dark:hover:text-black font-bold uppercase tracking-widest text-xs disabled:opacity-40 group"
      >
        {submitting ? (
          t("encrypting")
        ) : (
          <span className="inline-flex items-center gap-2">
            {t("encryptButton")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        )}
      </Button>
    </div>
  );
}

function ToggleRow({ testid, iconEl, labelKey, hintKey, checked, onCheckedChange, disabled, noBorder }) {
  const { t } = useLang();
  return (
    <div className={`flex items-start justify-between ${noBorder ? "" : "py-4 border-t border-black/10 dark:border-white/10"}`}>
      <div>
        <Label className="flex items-center gap-2 text-neutral-900 dark:text-neutral-100 text-sm">
          {iconEl} {t(labelKey)}
        </Label>
        <p className="text-xs text-neutral-500 mt-1">{t(hintKey)}</p>
      </div>
      <Switch
        data-testid={testid}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}
